<template>
    <div v-if="data.length">
        <LineChart
            :data="chartData"
            :options="chartOptions"
            :width="450"
            :height="300"
        ></LineChart>
    </div>
</template>

<script>
import { getCurrentInstance, toRefs, computed } from '@vue/composition-api'
import createVueChart from '@/components/CreateVueChart'
import formattedDate from '@/utils/formattedDate'
export default {
    components: {
        LineChart: createVueChart('Line')
    },
    props: {
        xKey: {
            type: String,
            default: undefined
        },
        data: {
            type: Array,
            default: function () {
                return []
            }
        },
        themeColors: {
            type: Object,
            default: function () {
                return {}
            }
        },
        theme: {
            type: String
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const yKeys = ['In', 'Out', 'Etc']
        const { data, themeColors } = toRefs(props)
        const colors = [
            themeColors.value.success,
            themeColors.value.warning,
            themeColors.value.danger
        ]
        const lang = computed(() => vm.$i18n.locale)
        const noData = computed(() => !data.value.length)
        const gDataValueMap = computed(() => {
            return data.value.reduce((acc, cur) => {
                acc[cur.date] = cur.types.reduce((subAcc, subCur) => {
                    subAcc[subCur.type] = {
                        cost: subCur.cost,
                        usageAmount: subCur.usageAmount
                    }
                    return subAcc
                }, {})
                return acc
            }, {})
        })
        const transparentize = (value, opacity) => {
            const r = parseInt(value.slice(1, 3), 16)
            const g = parseInt(value.slice(3, 5), 16)
            const b = parseInt(value.slice(5, 7), 16)
            return `rgba(${r}, ${g}, ${b}, ${opacity})`
        }
        const getYData = label => label < 1 ? vm.$_getDollar(label, 3) : vm.$_getDollar(label, 0)
        const labelToAmount = label => vm.$_getAmount(label, 'DataTransfer')
        const chartData = computed(() => {
            return {
                labels: data.value.map(item => item.date),
                datasets: [
                    ...yKeys.map((key, index) => {
                        return {
                            mode: 'index',
                            borderColor: colors[index % colors.length],
                            borderWidth: 1,
                            fill: false,
                            label: key,
                            pointBorderColor: colors[index % colors.length],
                            pointBackgroundColor: '#fff',
                            pointHoverBorderColor: colors[index % colors.length],
                            data: data.value.map(item => gDataValueMap.value[item.date][key]?.cost ?? NaN),
                            type: 'line',
                            yAxisID: 'y1'
                        }
                    }),
                    ...yKeys.map((key, index) => {
                        return {
                            mode: 'nearest',
                            label: key,
                            backgroundColor: transparentize(themeColors.value.primary, 0.3),
                            borderWidth: 0,
                            data: data.value.map(item => gDataValueMap.value[item.date][key]?.usageAmount ?? NaN),
                            type: 'bar',
                            barPercentage: 0.7,
                            stack: true,
                            yAxisID: 'y2'
                        }
                    })

                ]
            }
        })
        const chartOptions = computed(() => {
            return {
                layout: {
                    padding: {
                        top: 20,
                        bottom: 20,
                        left: 30,
                        right: 30
                    }
                },
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                    datalabels: {
                        display: false
                    }
                },
                legend: {
                    display: false
                },
                tooltips: {
                    mode: 'index',
                    callbacks: {
                        beforeTitle: ([tooltipItem]) => {
                            return tooltipItem.xLabel
                        },
                        title: ([tooltipItem], data) => {
                            const item = gDataValueMap.value[tooltipItem.label]
                            const TotalCost = item.In?.cost + item.Out?.cost + item.Etc?.cost ?? 0
                            const totalAmount = item.In?.usageAmount + item.Out?.usageAmount + item.Etc?.usageAmount ?? 0
                            return `Total: ${vm.$_getDollar(TotalCost, 2)} (${labelToAmount(totalAmount)})`
                        },
                        label: (tooltipItem, { datasets }) => {
                            const dataName = datasets[tooltipItem.datasetIndex].label
                            const dataValue = gDataValueMap.value[tooltipItem.label][dataName]
                            return tooltipItem.datasetIndex > 2 ? null : `${dataName} : ${vm.$_getDollar(dataValue.cost, 2)} (${labelToAmount(dataValue.usageAmount)})`
                        }
                    }
                },
                scales: {
                    xAxes: [
                        {
                            ticks: {
                                display: true,
                                callback: value => formattedDate(lang.value, value, 'month-day')
                            },
                            gridLines: {
                                display: true
                            }
                        }
                    ],
                    yAxes: [
                        {
                            stacked: false,
                            type: 'linear',
                            id: 'y1',
                            gridLines: {
                                display: true
                            },
                            ticks: {
                                beginAtZero: true,
                                count: 5,
                                maxTicksLimit: 3,
                                callback: getYData
                            }
                        },
                        {
                            stacked: true,
                            type: 'linear',
                            id: 'y2',
                            position: 'right',
                            gridLines: {
                                display: false
                            },
                            ticks: {
                                beginAtZero: true,
                                count: 5,
                                maxTicksLimit: 5,
                                callback: labelToAmount
                            }
                        }
                    ]
                }
            }
        })
        return {
            yKeys,
            chartData,
            chartOptions,
            noData
        }
    }
}
</script>

<style>

</style>
